import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box, Heading, HStack, VStack, Text, Badge, Flex,
  useToast, useColorModeValue, Button, Divider,
  Grid, GridItem, Alert, Stack, Radio, Checkbox, Textarea,
  Spacer, IconButton, Select, Tooltip,useBreakpointValue,Image,
} from "@chakra-ui/react";
import axios from "axios";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Choice, ExamAttemptedQuestionSet, AttemptedQuestion } from "../Common/types";
import { FaFlag, FaThLarge } from "react-icons/fa";
import { RenderQuestionContext, RenderTextWithImages, QuestionContext } from "../../utils/questionRendering";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const LegendItem = ({ color, label, flag = false, border = false }: {
  color: string; label: string; flag?: boolean; border?: boolean;
}) => (
  <Flex align="center" gap={2}>
    <Box position="relative" w="20px" h="20px">
      <Box w="100%" h="100%" borderRadius="full" bg={color}
        border={border ? '2px solid' : 'none'}
        borderColor={border ? color : 'transparent'} />
      {flag && (
        <Box position="absolute" top="-6px" right="-6px" color="purple.500" fontSize="sm">
          <FaFlag />
        </Box>
      )}
    </Box>
    <Text fontSize="sm" whiteSpace="nowrap">{label}</Text>
  </Flex>
);

export interface RendererStats {
  answeredWithFlag: number; answeredWithoutFlag: number;
  visitedWithFlag: number; visitedWithoutFlag: number;
  notVisited: number; total: number;
}

export interface RendererHandle {
  getStats: () => RendererStats;
  submitAll: () => Promise<void>;
}

// Defined at module scope, OUTSIDE the main component -- keeps its
// identity stable across re-renders (the exam timer ticking every
// second was otherwise forcing a remount every tick, which reset
// scroll position on the grid back to the top instantly).
const QuestionGridContent: React.FC<{
  attemptedQuestions: any[];
  currentQuestionIndex: number;
  handleQuestionClick: (index: number) => void;
  isMobile: boolean;
  setIsGridVisible: (v: boolean) => void;
}> = ({ attemptedQuestions, currentQuestionIndex, handleQuestionClick, isMobile, setIsGridVisible }) => (
  <>
    <Box flex="1" overflowY="auto" mb={4}>
      <Grid templateColumns="repeat(5, 1fr)" gap={2} autoRows="minmax(44px, auto)">
        {attemptedQuestions
       //   .sort((a, b) => a.sequence_no - b.sequence_no)
          .map((q, index) => {
            const isCurrent = index === currentQuestionIndex;
            const statusColor = ({
              NOT_VISITED: 'gray.400',
              VISITED: 'red.400',
              ANSWERED: 'green.400'
            } as Record<string, string>)[q.status] || 'gray.400';
            return (
              <GridItem key={q.exam_attempted_question_id}>
                <Flex direction="column" align="center" role="button"
                  _hover={{ transform: 'scale(1.05)', cursor: 'pointer' }}
                  transition="transform 0.2s"
                  onClick={() => {
                    handleQuestionClick(index);
                    if (isMobile) setIsGridVisible(false);
                  }}>
                  <Box position="relative">
                    <Box w="34px" h="34px" borderRadius="full" bg={statusColor}
                      border={isCurrent ? '3px solid' : 'none'}
                      borderColor={isCurrent ? 'blue.500' : 'transparent'}
                      display="flex" alignItems="center" justifyContent="center"
                      fontWeight="bold" color="white" fontSize="xs" boxShadow="md">
                      {q.sequence_no}
                    </Box>
                    {q.flagged && (
                      <Box position="absolute" top="-6px" right="-8px" fontSize="sm" color="purple.500">
                        <FaFlag />
                      </Box>
                    )}
                  </Box>
                </Flex>
              </GridItem>
            );
          })}
      </Grid>
    </Box>
    <Box borderTop="1px solid" borderColor="gray.200" pt={3}>
      <Text fontWeight="bold" mb={2} fontSize="sm">Legend</Text>
      <Grid templateColumns="1fr" gap={2}>
        <LegendItem color="gray.400" label="Not Visited" />
        <LegendItem color="red.400" label="Visited" />
        <LegendItem color="green.400" label="Answered" />
        <LegendItem color="red.400" label="Visited + Flagged" flag />
        <LegendItem color="green.400" label="Answered + Flagged" flag />
        <LegendItem color="blue.500" label="Current" border />
      </Grid>
    </Box>
  </>
);

interface QuestionSetRendererProps {
  examAttemptedQuestionSetProp: ExamAttemptedQuestionSet;
    onQuestionChange?: (index: number, question: AttemptedQuestion) => void;
  }

const GRID_PANE_WIDTH = "260px";
const FOOTER_HEIGHT = "56px";
const PANES_HEIGHT = "calc(100vh - 170px)";

const QuestionSetRenderer = forwardRef<RendererHandle, QuestionSetRendererProps>(
  ({ examAttemptedQuestionSetProp , onQuestionChange}, ref) => {

  // const [examAttemptedQuestionSet, setExamAttemptedQuestionSet] =
  //   useState<ExamAttemptedQuestionSet>(examAttemptedQuestionSetProp);

  const [examAttemptedQuestionSet, setExamAttemptedQuestionSet] =
    useState<ExamAttemptedQuestionSet>(() => ({
      ...examAttemptedQuestionSetProp,
      attempted_questions: [...examAttemptedQuestionSetProp.attempted_questions]
        .sort((a, b) => a.sequence_no - b.sequence_no),
    }));

  const [currentQuestionIndex, SetCurrentQuestionIndex] = useState<number>(0);
  const [isQuestionPaneCollapsed, setIsQuestionPaneCollapsed] = useState(false);
  const [isGridVisible, setIsGridVisible] = useState(true);

  const currentQuestion = examAttemptedQuestionSet.attempted_questions[currentQuestionIndex];

    // Add near top of component alongside existing state
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Grid is never visible on mobile regardless of user preference
  const effectiveGridVisible = isGridVisible && !isMobile;

  const collapseToggleBg = useColorModeValue("gray.50", "gray.750");
const collapseToggleHoverBg = useColorModeValue("gray.100", "gray.700");

  const hasValidContext = (ctx: any): ctx is QuestionContext =>
    Boolean(ctx) && typeof ctx === 'object' && 'type' in ctx;

  useEffect(() => {
    if (currentQuestion && onQuestionChange) {
      onQuestionChange(currentQuestionIndex, currentQuestion);
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    setIsQuestionPaneCollapsed(false);
  }, [currentQuestion?.exam_attempted_question_id]);

  useImperativeHandle(ref, () => ({
    getStats: () => {
      const all = examAttemptedQuestionSet.attempted_questions;
      const total = all.length;
      const answeredWithFlag    = all.filter(q => q.status === "ANSWERED" && q.flagged).length;
      const answeredWithoutFlag = all.filter(q => q.status === "ANSWERED" && !q.flagged).length;
      const visitedWithFlag     = all.filter(q => q.status === "VISITED"  && q.flagged).length;
      const visitedWithoutFlag  = all.filter(q => q.status === "VISITED"  && !q.flagged).length;
      const notVisited          = all.filter(q => q.status === "NOT_VISITED").length;
      return { total, answeredWithFlag, answeredWithoutFlag, visitedWithFlag, visitedWithoutFlag, notVisited };
    },

    submitAll: async () => {
      const { attempted_questions, exam_attempted_question_set_id } = examAttemptedQuestionSet;
      if (!attempted_questions?.length || !exam_attempted_question_set_id) return;
      if (currentQuestionIndex != null) {
        await saveAttemptToServer(attempted_questions[currentQuestionIndex]);
      }
      const answers_data = attempted_questions.map(q => ({
        exam_attempted_question_id: q.exam_attempted_question_id,
        selected_option_ids: q.selected_option_ids,
        selected_cloze_answers: q.selected_cloze_answers ?? null,
        text_response: q.text_response,
        time_spent: q.time_spent,
        flagged: q.flagged,
        status: q.status,
      }));
      try {
        await axios.put(
          `${apiUrl}/exam-question-set-attempt/${exam_attempted_question_set_id}/save`,
          answers_data,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
      } catch (err) {
        console.error("Bulk save failed:", err);
      }
    }
  }));

  const cardBg = useColorModeValue("gray.100", "gray.700");
  const questionPaneBg = useColorModeValue("white", "gray.700");
  const contextPaneBg = useColorModeValue("white", "gray.700");
  const footerBg = useColorModeValue("gray.100", "gray.800");
  const apiUrl = process.env.REACT_APP_API_URL;

  function updateLocalQuestion(
    prev: ExamAttemptedQuestionSet, index: number,
    patch: Partial<AttemptedQuestion>
  ): ExamAttemptedQuestionSet {
    if (!prev) return prev;
    const updatedQuestions = [...prev.attempted_questions];
    updatedQuestions[index] = { ...updatedQuestions[index], ...patch };
    return { ...prev, attempted_questions: updatedQuestions };
  }

  function updateQuestionLocally(patch: Partial<AttemptedQuestion>) {
    if (currentQuestionIndex != null)
      setExamAttemptedQuestionSet(prev => updateLocalQuestion(prev, currentQuestionIndex, patch));
  }

  function updateQuestionWithSave(patch: Partial<AttemptedQuestion>) {
    if (currentQuestionIndex != null) {
      setExamAttemptedQuestionSet(prev => {
        const updated = updateLocalQuestion(prev, currentQuestionIndex, patch);
        if (updated) debouncedSave(updated.attempted_questions[currentQuestionIndex]);
        return updated;
      });
    }
  }

  const [workingAnswer, setWorkingAnswer] = useState<{
    selected: number[]; text: string; cloze: Record<string, number | number[]>;
  }>({ selected: [], text: "", cloze: {} });

  function useQuestionTimer(currentQuestionId: number | undefined, onElapsed: (seconds: number) => void) {
    const startRef = React.useRef<number | null>(null);
    const cbRef = React.useRef(onElapsed);
    useEffect(() => { cbRef.current = onElapsed; }, [onElapsed]);
    useEffect(() => {
      if (currentQuestionId == null) return;
      startRef.current = Date.now();
      return () => {
        if (startRef.current != null) {
          const secs = (Date.now() - startRef.current) / 1000;
          cbRef.current(secs);
        }
      };
    }, [currentQuestionId]);
  }

  function debounceCallback<T extends any[]>(fn: (...args: T) => void, delay = 500) {
    let timer: NodeJS.Timeout | undefined;
    return (...args: T) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  async function saveAttemptToServer(q: AttemptedQuestion) {
    const payload = {
      exam_attempted_question_id: q.exam_attempted_question_id,
      selected_option_ids: q.selected_option_ids,
      selected_cloze_answers: q.selected_cloze_answers ?? null,
      text_response: q.text_response,
      time_spent: q.time_spent,
      flagged: q.flagged,
      status: q.status,
    };
    await axios.put(`${apiUrl}/exam-question-attempt/save`, payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
  }

  const debouncedSave = React.useMemo(() => debounceCallback(saveAttemptToServer, 500), []);

  useEffect(() => {
    if (!currentQuestion) return;
    if (currentQuestion.status === "NOT_VISITED") updateQuestionLocally({ status: "VISITED" });
    setWorkingAnswer({
      selected: currentQuestion.selected_option_ids ?? [],
      text: currentQuestion.text_response ?? "",
      cloze: currentQuestion.selected_cloze_answers ?? {},
    });
    if (currentQuestion.question_type === "WRITTEN_SUBMISSION" && currentQuestion.status !== "ANSWERED") {
      updateQuestionWithSave({ selected_option_ids: [], text_response: "", status: "ANSWERED" });
    }
  }, [currentQuestion?.exam_attempted_question_id]);

  useQuestionTimer(currentQuestion?.exam_attempted_question_id, (secs) => {
    if (!currentQuestion) return;
    const newTime = (currentQuestion.time_spent ?? 0) + secs;
    updateQuestionWithSave({ time_spent: newTime });
  });

  const handleQuestionClick = (index: number) => SetCurrentQuestionIndex(index);

  function replaceReferencesWithLabels(text: string, choiceIdToLabel: Record<number, string>): string {
    return text.replace(/@\{(\d+)\}/g, (_, id) => {
      const choiceId = parseInt(id);
      return choiceIdToLabel[choiceId] || `@{${id}}`;
    });
  }

  type ClozeSegment = { type: "text"; content: string } | { type: "dropdown"; id: string };

  function parseClozeText(text: string): ClozeSegment[] {
    const segments: ClozeSegment[] = [];
    const regex = /\{\{([DG]\d+)\}\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
      segments.push({ type: "dropdown", id: match[1] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) segments.push({ type: "text", content: text.slice(lastIndex) });
    return segments;
  }

  function getDropdownOptions(question: AttemptedQuestion, dropdownTag: string): { id: number; text: string }[] {
    if (question.question_type === "CLOZE_DROPDOWN") {
      const dropdownId = parseInt(dropdownTag.slice(1));
      const group = (question.choices as any[])?.find((g) => g.dropdown_id === dropdownId);
      return group?.options ?? [];
    }
    if (question.question_type === "CLOZE_GAP_FILL") {
      const pool = (question.choices as any)?.option_pool ?? [];
      const currentCloze = workingAnswer.cloze as Record<string, number>;
      const usedElsewhere = new Set(
        Object.entries(currentCloze).filter(([k]) => k !== dropdownTag).map(([, v]) => v)
      );
      return pool.filter((opt: any) => !usedElsewhere.has(opt.id));
    }
    return [];
  }

  function handleClozeSelect(dropdownTag: string, optionId: number) {
    const isGapFill = currentQuestion.question_type === "CLOZE_GAP_FILL";
    const nextCloze = isGapFill
      ? { ...workingAnswer.cloze, [dropdownTag]: optionId }
      : { ...workingAnswer.cloze, [dropdownTag]: [optionId] };
    setWorkingAnswer((p) => ({ ...p, cloze: nextCloze }));
    const allFilled = currentQuestion.question_type === "CLOZE_GAP_FILL"
      ? Object.keys(nextCloze).length === ((currentQuestion.choices as any)?.gap_count ?? 0)
      : Object.keys(nextCloze).length === ((currentQuestion.choices as any[])?.length ?? 0);
    updateQuestionWithSave({ selected_cloze_answers: nextCloze, status: allFilled ? "ANSWERED" : "VISITED" });
  }

  function renderClozePassage(question: AttemptedQuestion) {
    const segments = parseClozeText(question.question_text ?? "");
    return (
      <Text fontSize="sm" fontWeight="semibold" whiteSpace="pre-line" mb={3} lineHeight="2.4">
        {segments.map((seg, idx) => {
          if (seg.type === "text") return <React.Fragment key={idx}>{seg.content}</React.Fragment>;
          const options = getDropdownOptions(question, seg.id);
          const currentValue = (workingAnswer.cloze as Record<string, any>)[seg.id];
          const selectedValue = question.question_type === "CLOZE_GAP_FILL"
            ? currentValue
            : (Array.isArray(currentValue) ? currentValue[0] : currentValue);
          return (
            <Select key={idx} display="inline-block" verticalAlign="middle" size="sm"
              w="auto" minW="140px" maxW="260px" mx={1} placeholder="Select..."
              value={selectedValue ?? ""}
              onChange={(e) => handleClozeSelect(seg.id, parseInt(e.target.value))}
            >
              {options.map((opt) => <option key={opt.id} value={opt.id}>{opt.text}</option>)}
            </Select>
          );
        })}
      </Text>
    );
  }

  const isLastQuestion = currentQuestionIndex === examAttemptedQuestionSet.attempted_questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

/*   const QuestionGridContent = () => (
    <>
      <Box flex="1" overflowY="auto" mb={4}>
        <Grid templateColumns="repeat(5, 1fr)" gap={2} autoRows="minmax(44px, auto)">
          {examAttemptedQuestionSet.attempted_questions
            .sort((a, b) => a.sequence_no - b.sequence_no)
            .map((q, index) => {
              const isCurrent = index === currentQuestionIndex;
              const statusColor = ({
                NOT_VISITED: 'gray.400',
                VISITED: 'red.400',
                ANSWERED: 'green.400'
              } as Record<string, string>)[q.status] || 'gray.400';
              return (
                <GridItem key={q.exam_attempted_question_id}>
                  <Flex direction="column" align="center" role="button"
                    _hover={{ transform: 'scale(1.05)', cursor: 'pointer' }}
                    transition="transform 0.2s"
                    onClick={() => {
                      handleQuestionClick(index);
                      if (isMobile) setIsGridVisible(false); // close drawer after picking a question
                    }}>
                    <Box position="relative">
                      <Box w="34px" h="34px" borderRadius="full" bg={statusColor}
                        border={isCurrent ? '3px solid' : 'none'}
                        borderColor={isCurrent ? 'blue.500' : 'transparent'}
                        display="flex" alignItems="center" justifyContent="center"
                        fontWeight="bold" color="white" fontSize="xs" boxShadow="md">
                        {q.sequence_no}
                      </Box>
                      {q.flagged && (
                        <Box position="absolute" top="-6px" right="-8px" fontSize="sm" color="purple.500">
                          <FaFlag />
                        </Box>
                      )}
                    </Box>
                  </Flex>
                </GridItem>
              );
            })}
        </Grid>
      </Box>
      <Box borderTop="1px solid" borderColor="gray.200" pt={3}>
        <Text fontWeight="bold" mb={2} fontSize="sm">Legend</Text>
        <Grid templateColumns="1fr" gap={2}>
          <LegendItem color="gray.400" label="Not Visited" />
          <LegendItem color="red.400" label="Visited" />
          <LegendItem color="green.400" label="Answered" />
          <LegendItem color="red.400" label="Visited + Flagged" flag />
          <LegendItem color="green.400" label="Answered + Flagged" flag />
          <LegendItem color="blue.500" label="Current" border />
        </Grid>
      </Box>
    </>
  ); */
  
return (
  <Flex
    direction="column"
    w="100%"
    minH={isMobile ? "calc(100dvh - 95px)" : undefined}
  >

    {/* Panes row */}
    <Flex
      gap={0}
      h={isMobile ? "auto" : PANES_HEIGHT}
      flex={isMobile ? 1 : undefined}
      minH={isMobile ? 0 : undefined}
      w="100%"
      overflow={isMobile ? "visible" : "hidden"}
      direction={isMobile ? "column" : "row"}
    >

      {/* Context pane */}
      {currentQuestion?.question_context &&
        typeof currentQuestion.question_context === 'object' &&
        'type' in currentQuestion.question_context && (
        <Box
          w={isMobile ? "100%" : (
            isQuestionPaneCollapsed
              ? effectiveGridVisible ? `calc(100% - ${GRID_PANE_WIDTH} - 24px)` : "calc(100% - 24px)"
              : effectiveGridVisible ? "40%" : "50%"
          )}
          h={isMobile ? "auto" : "100%"}
          maxH={isMobile ? "none" : "100%"}
          flexShrink={0}
          overflowY={isMobile ? "visible" : "auto"}
          p={4}
          bg={contextPaneBg}
          borderRight={isMobile ? "none" : "1px solid"}
          borderBottom={isMobile ? "1px solid" : "none"}
          borderColor="gray.200"
        >
          <RenderQuestionContext
            context={currentQuestion.question_context as any}
            isMobile={isMobile}
          />
        </Box>
      )}

      {/* Collapse toggle -- desktop only, meaningless in vertical stack */}
      {!isMobile &&
        currentQuestion?.question_context &&
        typeof currentQuestion.question_context === 'object' &&
        'type' in currentQuestion.question_context && (
        <Flex
          w="16px"
          flexShrink={0}
          h="100%"
          direction="column"
          align="center"
          justify="center"
          bg={collapseToggleBg}
          borderRight="1px solid"
          borderColor="gray.200"
          cursor="pointer"
          onClick={() => setIsQuestionPaneCollapsed(c => !c)}
          _hover={{ bg: collapseToggleHoverBg }}
        >
          <Tooltip label={isQuestionPaneCollapsed ? "Show question" : "Expand context"}>
            <Box fontSize="12px" color="gray.500">
              {isQuestionPaneCollapsed ? <FaChevronLeft /> : <FaChevronRight />}
            </Box>
          </Tooltip>
        </Flex>
      )}

      {/* Question pane */}
      {(!isQuestionPaneCollapsed || isMobile) && (
        <Box
          flex={1}
          h={isMobile ? "auto" : "100%"}
          overflowY={isMobile ? "visible" : "auto"}
          p={4}
          bg={questionPaneBg}
          borderRight={effectiveGridVisible ? "1px solid" : "none"}
          borderColor="gray.200"
        >
          {currentQuestion ? (
            <>
              <Alert status="info" variant="left-accent" my={3} fontSize="sm">
                {(() => {
                  const { question_type, max_selections } = currentQuestion;
                  if (question_type === "MCQ_SINGLE") return "Select exactly one option.";
                  if (question_type === "MCQ_MULTI")
                    return max_selections
                      ? `Select up to ${max_selections} option${max_selections > 1 ? "s" : ""}.`
                      : "Select all options you think are correct.";
                  if (question_type === "WRITTEN_SUBMISSION")
                    return "Please submit your written answer sheet to the Exam Invigilator.";
                  if (question_type === "CLOZE_DROPDOWN")
                    return "Select the best word for each gap in the passage.";
                  if (question_type === "CLOZE_GAP_FILL")
                    return "Select the sentence that best completes each gap. Each option can only be used once.";
                  return "Type your answer below.";
                })()}
              </Alert>

              {(currentQuestion.question_type === "CLOZE_DROPDOWN" ||
                currentQuestion.question_type === "CLOZE_GAP_FILL") ? (
                renderClozePassage(currentQuestion)
              ) : (
                <RenderTextWithImages
                  text={currentQuestion.question_text ?? ""}
                  fontSize="sm"
                  fontWeight="semibold"
                />
              )}

              {currentQuestion.question_type.startsWith("MCQ") ? (
                <Stack spacing={3}>
                  {(currentQuestion.options_display_order ?? [])
                    .map((choiceId) => (currentQuestion.choices ?? []).find((c) => c.id === choiceId))
                    .filter((choice): choice is Choice => Boolean(choice))
                    .map((choice) => {
                      const choiceId = choice.id;
                      const isChecked = workingAnswer.selected.includes(choiceId);
                      const maxedOut = currentQuestion.question_type === "MCQ_MULTI" &&
                        currentQuestion.max_selections && !isChecked &&
                        workingAnswer.selected.length >= currentQuestion.max_selections;
                      const toggle = () => {
                        if (maxedOut) return;
                        let nextSel: number[];
                        if (currentQuestion.question_type === "MCQ_SINGLE") {
                          nextSel = [choiceId];
                        } else {
                          nextSel = isChecked
                            ? workingAnswer.selected.filter(id => id !== choiceId)
                            : [...workingAnswer.selected, choiceId];
                        }
                        setWorkingAnswer(p => ({ ...p, selected: nextSel }));
                        updateQuestionWithSave({
                          selected_option_ids: nextSel,
                          status: nextSel.length > 0 ? "ANSWERED" : "VISITED",
                        });
                      };
                      const choiceIdToLabel: Record<number, string> = {};
                      (currentQuestion.options_display_order ?? []).forEach((id, index) => {
                        choiceIdToLabel[id] = String.fromCharCode(65 + index);
                      });
                      return (
                        <Box key={choiceId}
                          border="1px solid"
                          borderColor={isChecked ? "blue.400" : "gray.300"}
                          borderRadius="md" p={2} w="100%"
                          opacity={maxedOut ? 0.5 : 1}
                          cursor={maxedOut ? "not-allowed" : "pointer"}
                          onClick={toggle}
                        >
                          <HStack>
                            {currentQuestion.question_type === "MCQ_SINGLE"
                              ? <Radio isChecked={isChecked} pointerEvents="none" />
                              : <Checkbox isChecked={isChecked} pointerEvents="none" />}
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="semibold" fontSize="sm">
                                {choiceIdToLabel[choice.id]}. {replaceReferencesWithLabels(choice.text ?? "", choiceIdToLabel)}
                              </Text>
                              {choice.image && (
                                <Image src={choice.image} alt="Option"
                                  maxW="100%" maxH="100px" borderRadius="md" />
                              )}
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                </Stack>
              ) : currentQuestion.question_type === "SHORT_ANSWER" ? (
                <Textarea value={workingAnswer.text}
                  onChange={e => setWorkingAnswer(p => ({ ...p, text: e.target.value }))}
                  onBlur={() => updateQuestionWithSave({
                    text_response: workingAnswer.text,
                    status: workingAnswer.text.trim() ? "ANSWERED" : "VISITED"
                  })}
                  placeholder="Type your answer here…" resize="vertical" rows={1} />
              ) : currentQuestion.question_type === "ESSAY_TEXT" ? (
                <Textarea value={workingAnswer.text}
                  onChange={e => setWorkingAnswer(p => ({ ...p, text: e.target.value }))}
                  onBlur={() => updateQuestionWithSave({
                    text_response: workingAnswer.text,
                    status: workingAnswer.text.trim() ? "ANSWERED" : "VISITED"
                  })}
                  placeholder="Type your essay response here…" resize="vertical" rows={10} spellCheck={false} />
              ) : currentQuestion.question_type === "WRITTEN_SUBMISSION" ? (
                <Box border="1px dashed gray" borderRadius="md" p={4} bg="gray.50" color="gray.700">
                  <Text fontSize="sm">
                    <strong>Written Submission:</strong> Once done, please hand over your answer sheet to the Exam Invigilator within stipulated time.
                  </Text>
                </Box>
              ) : null}
            </>
          ) : (
            <Text>No question selected.</Text>
          )}
        </Box>
      )}

      {/* Grid pane -- desktop only, never on mobile */}
      {effectiveGridVisible && !isMobile && (
        <Flex
          direction="column"
          w={GRID_PANE_WIDTH}
          minW={GRID_PANE_WIDTH}
          flexShrink={0}
          h="100%"
          bg={cardBg}
          p={4}
          overflowY="auto"
        >
          <Heading size="sm" mb={4}>Questions</Heading>
          <QuestionGridContent
            attemptedQuestions={examAttemptedQuestionSet.attempted_questions}
            currentQuestionIndex={currentQuestionIndex}
            handleQuestionClick={handleQuestionClick}
            isMobile={!!isMobile}
            setIsGridVisible={setIsGridVisible}
          />
        </Flex>
      )}

    </Flex>

    <Drawer
      isOpen={!!isMobile && isGridVisible}
      placement="right"
      onClose={() => setIsGridVisible(false)}
      size="xs"
    >
      <DrawerOverlay />
      <DrawerContent bg={cardBg}>
        <DrawerCloseButton />
        <DrawerHeader fontSize="sm">Questions</DrawerHeader>
        <DrawerBody display="flex" flexDirection="column" pb={4}>
          <QuestionGridContent
            attemptedQuestions={examAttemptedQuestionSet.attempted_questions}
            currentQuestionIndex={currentQuestionIndex}
            handleQuestionClick={handleQuestionClick}
            isMobile={!!isMobile}
            setIsGridVisible={setIsGridVisible}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>

    {/* Footer */}
    <Flex
      h={FOOTER_HEIGHT}
      w="100%"
      bg={footerBg}
      borderTop="2px solid"
      borderColor="gray.400"
      align="center"
      justify="space-between"
      px={{ base: 3, md: 6 }}
      flexShrink={0}
      boxShadow="0 -2px 8px rgba(0,0,0,0.08)"
      mt={isMobile ? 0 : 2}
      position="sticky"
      bottom={0}
      zIndex={20}
    >
      <Button
        size={{ base: "sm", md: "lg" }}
        onClick={() => SetCurrentQuestionIndex(i => Math.max(i - 1, 0))}
        isDisabled={isFirstQuestion}
        minW={{ base: "70px", md: "100px" }}
        colorScheme="blue"
      >
        ← Prev
      </Button>

      <HStack spacing={{ base: 2, md: 4 }}>
        {currentQuestion && (
          <Tooltip label={currentQuestion.flagged ? "Remove flag" : "Flag for review"}>
            <Button
              size={{ base: "sm", md: "lg" }}
              leftIcon={<FaFlag />}
              colorScheme={currentQuestion.flagged ? "purple" : "blue"}
              variant={currentQuestion.flagged ? "solid" : "outline"}
              onClick={() => updateQuestionWithSave({ flagged: !currentQuestion.flagged })}
            >
              <Box display={{ base: "none", md: "inline" }}>
                {currentQuestion.flagged ? "Flagged" : "Flag"}
              </Box>
            </Button>
          </Tooltip>
        )}

        <Tooltip label={isGridVisible ? "Hide question grid" : "Show question grid"}>
          <Button
            size={{ base: "sm", md: "lg" }}
            leftIcon={<FaThLarge />}
            colorScheme={isGridVisible ? "purple" : "blue"}
            variant={isGridVisible ? "solid" : "outline"}
            onClick={() => setIsGridVisible(v => !v)}
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="semibold"
          >
            <Box display={{ base: "none", md: "inline" }}>
              {isGridVisible ? "Hide Grid" : "Show Grid"}
            </Box>
          </Button>
        </Tooltip>
      </HStack>

      <Button
        size={{ base: "sm", md: "lg" }}
        onClick={() => SetCurrentQuestionIndex(i => Math.min(i + 1, examAttemptedQuestionSet.attempted_questions.length - 1))}
        isDisabled={isLastQuestion}
        minW={{ base: "70px", md: "100px" }}
        colorScheme="blue"
      >
        Next →
      </Button>
    </Flex>
  </Flex>
);
});

export default QuestionSetRenderer;